import type { TimelineLaneFocusState, TimelineTrack } from "@/state/timeline/timeline.types";

export type ImportedLaneKind = "video" | "audio" | "image" | "npc";

function uniqueTrackIds(trackIds: string[]) {
  return Array.from(new Set(trackIds.map((trackId) => String(trackId ?? "").trim()).filter(Boolean)));
}

function findTrackIdsByKind(tracks: TimelineTrack[], kind: TimelineTrack["kind"]) {
  return tracks
    .filter((track) => track.kind === kind)
    .map((track) => String(track.id ?? "").trim())
    .filter(Boolean);
}

export function createImportedLaneFocus(args: {
  tracks: TimelineTrack[];
  importedKind: ImportedLaneKind;
  sourceClipId: string;
  includeAudioCompanion?: boolean;
}): TimelineLaneFocusState {
  const { tracks, importedKind, sourceClipId, includeAudioCompanion = false } = args;

  const videoTrackIds = findTrackIdsByKind(tracks, "video");
  const audioTrackIds = findTrackIdsByKind(tracks, "audio");
  const npcTrackIds = findTrackIdsByKind(tracks, "npc");

  let visibleTrackIds: string[] = [];
  if (importedKind === "audio") {
    visibleTrackIds = audioTrackIds;
  } else if (importedKind === "npc") {
    visibleTrackIds = npcTrackIds;
  } else {
    visibleTrackIds = videoTrackIds;
    if (includeAudioCompanion) visibleTrackIds = [...visibleTrackIds, ...audioTrackIds];
  }

  const resolvedVisibleTrackIds = uniqueTrackIds(visibleTrackIds);
  const anchorTrackId = resolvedVisibleTrackIds[0] ?? null;

  if (resolvedVisibleTrackIds.length === 0) {
    return {
      mode: "all",
      visibleTrackIds: [],
      anchorTrackId: null,
      hideAuxiliaryLanes: false,
      sourceClipId,
    };
  }

  return {
    mode: "import",
    visibleTrackIds: resolvedVisibleTrackIds,
    anchorTrackId,
    hideAuxiliaryLanes: true,
    sourceClipId,
  };
}
